/* eslint-disable */
import * as d3 from "d3";
import { useEffect, useMemo, useRef, useState } from "react";
import { BUBBLE_LABEL_FONT, PALETTE, computePopoverPos } from "../discordBubbleUtils";
import { XboxArt, XboxArtPlaceholder, changeTone, ESTIMATED_PLAYERS_DISCLOSURE, fmtChange, fmtEstimated, fmtInt, fmtShare, typeLabel } from "./xboxUi";

export default function XboxMarketMind({ rows, loading, chartIds, onToggleChart }) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const nodes = useMemo(() => {
    const { width, height } = size;
    const data = rows.filter((r) => r.activeUsers > 0);
    if (!width || !height || !data.length) return [];

    // Same centered-square + 6% inset trick as the Steam/Discord bubble views.
    const side = Math.min(width, height);
    const inset = side * 0.06;
    const S = side - inset * 2;
    const ox = (width - S) / 2;
    const oy = (height - S) / 2;

    const interp = d3.piecewise(d3.interpolateRgb.gamma(2.2), PALETTE);
    const shares = data.map((r) => r.share).sort(d3.ascending);
    const lo = Math.max(1e-4, d3.quantile(shares, 0.02) ?? shares[0]);
    const hi = Math.max(lo * 10, d3.quantile(shares, 0.98) ?? shares[shares.length - 1]);
    const t = d3.scaleLog().domain([lo, hi]).range([0, 1]).clamp(true);

    const root = d3.hierarchy({ children: data }).sum((d) => d.activeUsers || 0);
    d3.pack().size([S, S]).padding(2)(root);
    return root.leaves().map((leaf) => ({
      row: leaf.data,
      x: leaf.x + ox,
      y: leaf.y + oy,
      r: leaf.r,
      color: interp(t(Math.max(lo, leaf.data.share))),
    }));
  }, [rows, size]);

  const pop = hover
    ? computePopoverPos({ x: hover.x, y: hover.y, r: hover.r, containerWidth: size.width, containerHeight: size.height })
    : null;

  return (
    <div ref={wrapRef} className="xbox-market" onMouseLeave={() => setHover(null)}>
      {loading ? (
        <div className="xbox-market-loading">Loading Xbox activity…</div>
      ) : nodes.length === 0 ? (
        <div className="xbox-market-loading">No titles match the current filters.</div>
      ) : (
        <svg width={size.width} height={size.height}>
          {nodes.map((n) => {
            const selected = chartIds.includes(n.row.titleId);
            const showName = n.r >= 26;
            const showShare = n.r >= 38;
            const fontSize = Math.max(10, Math.min(20, n.r / 4.2));
            const maxChars = Math.max(4, Math.floor((n.r * 1.7) / (fontSize * 0.56)));
            const name = n.row.title.name.length > maxChars ? `${n.row.title.name.slice(0, maxChars - 1)}…` : n.row.title.name;
            return (
              <g
                key={n.row.titleId}
                data-title-id={n.row.titleId}
                transform={`translate(${n.x},${n.y})`}
                onMouseEnter={() => setHover(n)}
                onClick={() => onToggleChart?.(n.row.titleId)}
                style={{ cursor: "pointer" }}
              >
                <title>{n.row.title.name}: {fmtShare(n.row.share)} player share</title>
                <circle
                  r={n.r}
                  fill={n.color}
                  fillOpacity={hover && hover.row.titleId !== n.row.titleId ? 0.55 : 0.92}
                  stroke={selected ? "#fff" : "rgba(0,0,0,0.55)"}
                  strokeWidth={selected ? 2.5 : 1}
                />
                {showName && (
                  <text
                    textAnchor="middle"
                    dy={showShare ? -fontSize * 0.15 : fontSize * 0.35}
                    fontSize={fontSize}
                    fontWeight={800}
                    fontFamily={BUBBLE_LABEL_FONT}
                    fill="#fff"
                    style={{ pointerEvents: "none", paintOrder: "stroke", stroke: "rgba(0,0,0,0.45)", strokeWidth: 3 }}
                  >
                    {name}
                  </text>
                )}
                {showShare && (
                  <text
                    textAnchor="middle"
                    dy={fontSize * 1.05}
                    fontSize={fontSize * 0.78}
                    fontWeight={700}
                    fontFamily={BUBBLE_LABEL_FONT}
                    fill="rgba(255,255,255,0.86)"
                    style={{ pointerEvents: "none" }}
                  >
                    {fmtShare(n.row.share)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}

      {hover && pop && (
        <div className="xbox-bubble-pop" style={{ left: pop.left, top: pop.top, width: pop.width }}>
          <div className="xbox-bubble-pop-art">
            <XboxArt candidates={hover.row.title.mainArt} width={200} fallback={<XboxArtPlaceholder />} />
          </div>
          <div className="xbox-bubble-pop-name">{hover.row.title.name}</div>
          <div className="xbox-muted">
            #{hover.row.rank} · {typeLabel(hover.row.title.type)}
            {hover.row.title.publisher ? ` · ${hover.row.title.publisher}` : ""}
          </div>
          <div className="xbox-bubble-pop-stats">
            <div><span>Player Share</span><b>{fmtShare(hover.row.share)}</b></div>
            <div title={ESTIMATED_PLAYERS_DISCLOSURE}><span>Est. Players</span><b>{fmtEstimated(hover.row.estimatedPlayers)}</b></div>
            <div><span>Observed</span><b>{fmtInt(hover.row.activeUsers)}</b></div>
            <div>
              <span>Change</span>
              <b className={`xbox-change is-${hover.row.isNew ? "new" : changeTone(hover.row.change)}`}>
                {hover.row.isNew ? "New" : fmtChange(hover.row.change)}
              </b>
            </div>
          </div>
          <div className="xbox-muted xbox-bubble-pop-hint">
            Click to {chartIds.includes(hover.row.titleId) ? "remove from" : "add to"} the history chart
          </div>
        </div>
      )}
    </div>
  );
}
